// ---------------------------------------------------------
//  Friends Screen -- Friend management
// ---------------------------------------------------------

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { t } from "@/lib/i18n";
import {
  Search, UserPlus, Check, X, Flame, Eye, Trash2,
  Users, Mail, Award, ArrowLeft,
} from "lucide-react";

interface Friend {
  id: string;
  friendshipId: string;
  name: string;
  email: string;
  totalPoints: number;
  streak: number;
}

interface FriendRequest {
  id: string;
  friendshipId: string;
  name: string;
  email: string;
}

interface FriendsScreenProps {
  friends: Friend[];
  requests: FriendRequest[];
  onAccept: (friendshipId: string) => void;
  onDecline: (friendshipId: string) => void;
  onSendRequest: (email: string) => void;
  onRemove: (friendshipId: string) => void;
  onViewGoals: (userId: string) => void;
  onBack?: () => void;
}

export function FriendsScreen({
  friends, requests, onAccept, onDecline, onSendRequest, onRemove, onViewGoals, onBack,
}: FriendsScreenProps) {
  const [searchEmail, setSearchEmail] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const handleSendRequest = () => {
    const email = searchEmail.trim();
    if (!email) return;
    onSendRequest(email);
    setSearchEmail("");
    setSent(true);
    setTimeout(() => setSent(false), 2500);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSendRequest();
    }
  };

  const filteredFriends = searchFilter
    ? friends.filter(f =>
        f.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
        f.email.toLowerCase().includes(searchFilter.toLowerCase()),
      )
    : friends;

  return (
    <div className="pb-24 px-4 pt-6">
      {/* Header */}
      <h1 className="text-xl font-bold mb-6 flex items-center gap-2">
        {onBack && (
          <button onClick={onBack} className="w-8 h-8 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80 transition-colors mr-1" aria-label="Geri">
            <ArrowLeft size={16} />
          </button>
        )}
        <Users size={22} className="text-gold" />
        {t("friends")}
      </h1>

      {/* Add friend by email */}
      <Card className="mb-6 border-gold/20">
        <CardContent className="p-4">
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">
            {t("add_friend")}
          </p>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchEmail}
                onChange={e => { setSearchEmail(e.target.value); setSent(false); }}
                onKeyDown={handleKeyDown}
                placeholder="E-posta adresi girin..."
                className="pl-9 h-11 rounded-xl"
                type="email"
                aria-label={t("add_friend")}
              />
            </div>
            <Button
              onClick={handleSendRequest}
              disabled={!searchEmail.trim() || sent}
              size="icon"
              className="w-11 h-11 rounded-xl bg-gold hover:bg-gold/90 text-ehille-dark shrink-0"
              aria-label="Istek gonder"
            >
              {sent ? <Check size={18} /> : <UserPlus size={18} />}
            </Button>
          </div>
          {sent && (
            <p className="text-[10px] text-ehille-green font-medium mt-2">
              Arkadas istegi gonderildi!
            </p>
          )}
        </CardContent>
      </Card>

      {/* Friend Requests */}
      {requests.length > 0 && (
        <Card className="mb-6 border-ehille-orange/20">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                {t("friend_requests")}
                <Badge variant="warning" className="text-[9px] h-4">{requests.length}</Badge>
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {requests.map((req) => (
              <div
                key={req.friendshipId}
                className="flex items-center gap-3 p-3 rounded-xl bg-ehille-orange/5 border border-ehille-orange/10"
              >
                <div className="w-10 h-10 rounded-full bg-ehille-orange/20 flex items-center justify-center text-sm font-bold text-ehille-orange shrink-0">
                  {req.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{req.name}</p>
                  <p className="text-[10px] text-muted-foreground truncate">{req.email}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button
                    size="icon"
                    className="w-8 h-8 rounded-lg bg-ehille-green hover:bg-ehille-green/90 text-white"
                    onClick={() => onAccept(req.friendshipId)}
                    aria-label={t("accept")}
                  >
                    <Check size={14} />
                  </Button>
                  <Button
                    size="icon"
                    variant="outline"
                    className="w-8 h-8 rounded-lg border-ehille-red/30 text-ehille-red hover:bg-ehille-red/10"
                    onClick={() => onDecline(req.friendshipId)}
                    aria-label={t("reject")}
                  >
                    <X size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Friends List */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">
              {t("friends")} ({friends.length})
            </CardTitle>
          </div>
          {friends.length > 3 && (
            <div className="relative mt-2">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                value={searchFilter}
                onChange={e => setSearchFilter(e.target.value)}
                placeholder={t("search_friends")}
                className="w-full h-9 rounded-lg border border-input bg-background pl-9 pr-3 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-1">
          {filteredFriends.length === 0 && friends.length === 0 && (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
                <Users size={24} className="text-muted-foreground" />
              </div>
              <p className="text-sm text-muted-foreground">Henuz arkadasiniz yok</p>
              <p className="text-[10px] text-muted-foreground mt-1">
                E-posta adresiyle arkadas ekleyin
              </p>
            </div>
          )}

          {filteredFriends.length === 0 && friends.length > 0 && (
            <div className="text-center py-6">
              <p className="text-sm text-muted-foreground">Sonuc bulunamadi</p>
            </div>
          )}

          {filteredFriends.map((friend) => (
            <div
              key={friend.friendshipId}
              className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-muted/50 transition-colors group"
            >
              {/* Avatar */}
              <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center text-sm font-bold text-gold shrink-0">
                {friend.name.charAt(0).toUpperCase()}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{friend.name}</p>
                <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                  <span className="flex items-center gap-0.5">
                    <Award size={9} className="text-gold" />
                    {friend.totalPoints} {t("points")}
                  </span>
                  {friend.streak > 0 && (
                    <span className="flex items-center gap-0.5">
                      <Flame size={9} className="text-ehille-orange" />
                      {friend.streak}
                    </span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-[10px]"
                  onClick={() => onViewGoals(friend.id)}
                >
                  <Eye size={12} className="mr-1" /> {t("view_goals")}
                </Button>

                {/* Remove button with confirmation */}
                {confirmRemove === friend.friendshipId ? (
                  <div className="flex items-center gap-1">
                    <Button
                      size="icon"
                      className="w-7 h-7 rounded-lg bg-ehille-red hover:bg-ehille-red/90 text-white"
                      onClick={() => { onRemove(friend.friendshipId); setConfirmRemove(null); }}
                      aria-label="Kaldirilmayi onayla"
                    >
                      <Check size={12} />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="w-7 h-7 rounded-lg"
                      onClick={() => setConfirmRemove(null)}
                      aria-label="Iptal"
                    >
                      <X size={12} />
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="w-7 h-7 rounded-lg text-muted-foreground hover:text-ehille-red opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => setConfirmRemove(friend.friendshipId)}
                    aria-label="Arkadasi kaldir"
                  >
                    <Trash2 size={12} />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
